import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { errorMessage } from '../helpers';

const DEMO_USER = { uid: 'demo-user', isDemo: true };

// Direct demo link: /demo opens the sandbox straight away, skipping the landing.
export const isDemoPath = () => {
    try {
        return window.location.pathname.replace(/\/+$/, '') === '/demo';
    } catch {
        return false;
    }
};

export const useAuth = (t) => {
    const [startInDemo] = useState(isDemoPath);
    const [currentUser, setCurrentUser] = useState(startInDemo ? DEMO_USER : null);
    const [userId, setUserId] = useState(startInDemo ? DEMO_USER.uid : null);
    const [isAuthReady, setIsAuthReady] = useState(startInDemo);
    const [isDemoMode, setIsDemoMode] = useState(startInDemo);

    const handleEnterDemoMode = useCallback(() => {
        setIsDemoMode(true);
        setCurrentUser(DEMO_USER);
        setUserId(DEMO_USER.uid);
        setIsAuthReady(true);
    }, []);
    
    const handleSignOut = useCallback(async (showModal) => {
        if (isDemoMode) {
            // Leaving the demo from /demo must not drop straight back into it.
            if (isDemoPath()) window.history.replaceState({}, '', '/');
            setIsDemoMode(false);
            setCurrentUser(null);
            setUserId(null);
            setIsAuthReady(false); 
            return;
        }
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Sign out Error:", error);
            showModal(t('auth.errorSignOut', { message: errorMessage(t, error) }));
        }
    }, [isDemoMode, t]);

    useEffect(() => {
        if (isDemoMode) return;

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user);
                setUserId(user.uid);
            } else {
                setCurrentUser(null);
                setUserId(null);
                setIsDemoMode(false);
            }
            setIsAuthReady(true);
        });

        return () => unsubscribe();
    }, [isDemoMode]);

    return { currentUser, userId, isAuthReady, isDemoMode, handleEnterDemoMode, handleSignOut };
};